import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
} from "@material-ui/core";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import MaskAndValid from "utils/maskAndValid";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

const statusHomologacaoOptions = [
  { value: "ATIVO", label: "Ativo" },
  { value: "INATIVO", label: "Inativo" },
  { value: "EM_AVALIACAO", label: "Em Avaliação" },
  { value: "PENDENTE", label: "Pendente" },
];

export default function DialogSupplier({
  open,
  onClose,
  onSaved,
  supplier,
  categories = [],
  readOnly = false,
}) {
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nomeFantasia: "",
    razaoSocial: "",
    cnpj: "",
    contato: "",
    telefone: "",
    email: "",
    site: "",
    categoriaSegmento: null,
    status: null,
    observacoes: "",
  });

  useEffect(() => {
    if (!open) return;
    if (supplier) {
      const category = categories.find(
        (c) => c.IdFranquiaCategoriaFornecedor === supplier.IdCategoriaFornecedor
      );
      
      const cnpjValue = supplier.Cnpj || "";
      const cnpjMasked = cnpjValue ? (cnpjValue.includes(".") || cnpjValue.includes("-") 
        ? cnpjValue 
        : MaskAndValid.maskCNPJ(cnpjValue)) : "";
      
      const telefoneValue = supplier.Telefone || "";
      const telefoneMasked = telefoneValue ? (telefoneValue.includes("(") || telefoneValue.includes("-")
        ? telefoneValue
        : MaskAndValid.maskPhone(telefoneValue)) : "";

      setForm({
        nomeFantasia: supplier.Nome || supplier.NomeFantasia || "",
        razaoSocial: supplier.RazaoSocial || "",
        cnpj: cnpjMasked,
        contato: supplier.Contato || supplier.ContatoPrincipal || "",
        telefone: telefoneMasked,
        email: supplier.Email || "",
        site: supplier.Site || "",
        categoriaSegmento: category || null,
        status:
          statusHomologacaoOptions.find((o) => o.value === (supplier.Status || supplier.StatusHomologacao)) || null,
        observacoes: supplier.Observacao || supplier.Observacoes || "",
      });
    } else {
      setForm({
        nomeFantasia: "",
        razaoSocial: "",
        cnpj: "",
        contato: "",
        telefone: "",
        email: "",
        site: "",
        categoriaSegmento: null,
        status: null,
        observacoes: "",
      });
    }
  }, [supplier, open, categories]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.nomeFantasia?.trim()) {
      snackbar.showMessage("Nome Fantasia é obrigatório.", { severity: "error" });
      return;
    }
    if (!form.razaoSocial?.trim()) {
      snackbar.showMessage("Razão Social é obrigatória.", { severity: "error" });
      return;
    }
    if (!form.cnpj?.trim()) {
      snackbar.showMessage("CNPJ é obrigatório.", { severity: "error" });
      return;
    }
    if (!MaskAndValid.validateCNPJ(form.cnpj)) {
      snackbar.showMessage("CNPJ inválido.", { severity: "error" });
      return;
    }
    if (!form.contato?.trim()) {
      snackbar.showMessage("Nome do Contato Principal é obrigatório.", { severity: "error" });
      return;
    }
    if (!form.telefone?.trim()) {
      snackbar.showMessage("Telefone é obrigatório.", { severity: "error" });
      return;
    }
    if (!MaskAndValid.validatePhone(form.telefone)) {
      snackbar.showMessage("Telefone inválido. Informe com DDD (ex: (11) 99999-9999).", { severity: "error" });
      return;
    }
    if (!form.email?.trim()) {
      snackbar.showMessage("E-mail é obrigatório.", { severity: "error" });
      return;
    }
    if (!MaskAndValid.validateEmail(form.email)) {
      snackbar.showMessage("E-mail inválido.", { severity: "error" });
      return;
    }
    if (!form.categoriaSegmento?.IdFranquiaCategoriaFornecedor) {
      snackbar.showMessage("Categoria é obrigatória.", { severity: "error" });
      return;
    }
    if (!form.status?.value) {
      snackbar.showMessage("Status é obrigatório.", { severity: "error" });
      return;
    }

    setSaving(true);
    try {
      const cnpjUnmasked = form.cnpj.replace(/[^\d]+/g, "");
      const telefoneUnmasked = form.telefone.replace(/[^\d]+/g, "");

      const body = {
        nomeFantasia: form.nomeFantasia.trim(),
        razaoSocial: form.razaoSocial.trim(),
        cnpj: cnpjUnmasked,
        email: form.email.trim(),
        idCategoriaFornecedor: form.categoriaSegmento.IdFranquiaCategoriaFornecedor,
        telefone: telefoneUnmasked,
        contatoPrincipal: form.contato.trim(),
        site: form.site?.trim() || "",
        statusHomologacao: form.status.value,
        observacao: form.observacoes?.trim() || "",
      };

      if (supplier?.IdFornecedor) {
        body.idFranquiaFornecedor = supplier.IdFornecedor;
        await Api.put("edit-supplier", "franquias", body, true);
      } else {
        await Api.post("create-supplier", "franquias", body, true);
      }

      snackbar.showMessage(
        supplier ? "Fornecedor atualizado com sucesso." : "Fornecedor criado com sucesso.",
        { severity: "success" }
      );
      if (onSaved) onSaved();
      else onClose();
    } catch (error) {
      snackbar.showMessage(error?.toString() || "Erro ao salvar fornecedor.", {
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const isViewMode = readOnly;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="dialog-fornecedor-title"
      maxWidth="lg"
      fullWidth
    >
      <DialogContent style={{ overflowY: "hidden" }}>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: isViewMode ? 0 : 8 }}>
          Dados Básicos
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Nome Fantasia"
              value={form.nomeFantasia}
              onChange={(e) => updateForm("nomeFantasia", e.target.value)}
              placeholder="Nome Fantasia"
              disabled={saving || isViewMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Razão Social"
              value={form.razaoSocial}
              onChange={(e) => updateForm("razaoSocial", e.target.value)}
              placeholder="Razão Social"
              disabled={saving || isViewMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="CNPJ"
              value={form.cnpj}
              onChange={(e) => updateForm("cnpj", MaskAndValid.maskCNPJ(e.target.value))}
              placeholder="XX.XXX.XXX/XXXX-XX"
              disabled={saving || !!supplier || isViewMode}
              error={!isViewMode && !!form.cnpj && !MaskAndValid.validateCNPJ(form.cnpj)}
              errorMessage={
                !isViewMode && form.cnpj && !MaskAndValid.validateCNPJ(form.cnpj) ? "CNPJ inválido" : ""
              }
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}>
          Contato
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Nome do Contato Principal"
              value={form.contato}
              onChange={(e) => updateForm("contato", e.target.value)}
              placeholder="Nome do Contato Principal"
              disabled={saving || isViewMode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Telefone"
              value={form.telefone}
              onChange={(e) => updateForm("telefone", MaskAndValid.maskPhone(e.target.value))}
              placeholder="(00) 00000-0000"
              disabled={saving || isViewMode}
              error={!isViewMode && !!form.telefone && !MaskAndValid.validatePhone(form.telefone)}
              errorMessage={
                !isViewMode && form.telefone && !MaskAndValid.validatePhone(form.telefone)
                  ? "Informe com DDD (ex: (99) 99999-9999)"
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="E-mail"
              value={form.email}
              onChange={(e) => updateForm("email", e.target.value)}
              placeholder="email@exemplo.com"
              type="email"
              disabled={saving || isViewMode}
              error={!isViewMode && !!form.email && !MaskAndValid.validateEmail(form.email)}
              errorMessage={!isViewMode && form.email && !MaskAndValid.validateEmail(form.email) ? "E-mail inválido" : ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Site/Link"
              value={form.site}
              onChange={(e) => updateForm("site", e.target.value)}
              placeholder="https://..."
              disabled={saving || isViewMode}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}>
          Classificação
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoCompleteField
              label="Categoria / Segmento do Produto"
              value={form.categoriaSegmento}
              onChange={(v) => updateForm("categoriaSegmento", v)}
              options={categories}
              optionLabelKey="Nome"
              optionIdKey="IdFranquiaCategoriaFornecedor"
              disabled={saving || isViewMode}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}>
          Homologação
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <AutoCompleteField
              label="Status"
              value={form.status}
              onChange={(v) => updateForm("status", v)}
              options={statusHomologacaoOptions}
              optionLabelKey="label"
              optionIdKey="value"
              disabled={saving || isViewMode}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}>
          Observações
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputText
              label="Observações"
              value={form.observacoes}
              onChange={(e) => updateForm("observacoes", e.target.value)}
              placeholder="Observações"
              multiline
              rows={4}
              disabled={saving || isViewMode}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: "16px 24px" }}>
        {isViewMode ? (
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        ) : (
          <>
            <Button onClick={onClose} color="primary" disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
              Salvar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
